"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Download, FileText } from "lucide-react";
import { DesignFile } from "@/types";

interface DesignFileDownloadsProps {
  designFiles: DesignFile[];
  productTitle?: string;
}

export function DesignFileDownloads({
  designFiles,
  productTitle,
}: DesignFileDownloadsProps) {
  if (!designFiles || designFiles.length === 0) {
    return null;
  }

  const handleDownload = (file: DesignFile) => {
    // Create a temporary link and trigger download
    const link = document.createElement("a");
    link.href = file.url;
    link.download = file.name;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // If only one design file, show a simple download button
  if (designFiles.length === 1) {
    const file = designFiles[0];
    return (
      <Button
        variant='outline'
        size='sm'
        onClick={() => handleDownload(file)}
        className='flex items-center justify-center'
        title={`Download ${file.name}`}
      >
        <Download className='h-4 w-4' />
      </Button>
    );
  }

  // If multiple design files, show a popover with all files
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className='flex items-center justify-center'
          title={`Download design files (${designFiles.length} files)`}
        >
          <Download className='h-4 w-4 mr-1' />
          <span className='text-xs'>{designFiles.length}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-80' align='end'>
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <FileText className='h-4 w-4' />
            <h4 className='font-medium text-sm'>Design Files</h4>
          </div>
          {productTitle && (
            <p className='text-xs text-muted-foreground'>{productTitle}</p>
          )}
          <div className='space-y-2'>
            {designFiles.map((file, index) => (
              <div
                key={index}
                className='flex items-center justify-between p-2 rounded border bg-muted/30'
              >
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium truncate' title={file.name}>
                    {file.name}
                  </p>
                </div>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => handleDownload(file)}
                  className='ml-2 h-8 w-8 p-0'
                  title={`Download ${file.name}`}
                >
                  <Download className='h-3 w-3' />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
